<template>
  <div class="invoice-wrapper">
    <!-- LOADING -->
    <div v-if="loading" class="pa-6 text-center">
      <v-progress-circular indeterminate color="primary" />
      <p class="mt-3 grey--text">Loading landlord data…</p>
    </div>

    <!-- NOT FOUND -->
    <div v-else-if="!landlord" class="pa-6 text-center">
      <v-icon large color="grey">mdi-alert-circle-outline</v-icon>
      <p class="mt-3 grey--text">Landlord not found.</p>
      <v-btn text @click="$router.back()">Go Back</v-btn>
    </div>

    <template v-else>
      <!-- ====== TABS ====== -->
      <div class="no-print tabs-wrapper">
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          color="primary"
          grow
          class="invoice-tabs"
        >
          <v-tab>
            <v-icon left small>mdi-file-document-plus-outline</v-icon>
            New Invoice
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-history</v-icon>
            History
            <v-chip
              v-if="sendHistory.length > 0"
              x-small
              color="primary"
              class="ml-2"
              dark
            >{{ sendHistory.length }}</v-chip>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-receipt</v-icon>
            Statement
          </v-tab>
        </v-tabs>
      </div>

      <v-tabs-items v-model="activeTab" class="tabs-content">

        <!-- ============================================================
             TAB 1 — NEW INVOICE / VIEW HISTORY
             ============================================================ -->
        <v-tab-item>
          <!-- BANNER: showing a historical invoice -->
          <div v-if="viewingHistoryInvoice" class="no-print controls-bar">
            <v-alert type="info" outlined dense class="mb-3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  Viewing invoice <strong>{{ invoiceNumber }}</strong> from history — line items
                  are frozen as sent.
                </div>
                <v-btn text x-small color="primary" @click="exitHistoryView">
                  Back to new invoice
                </v-btn>
              </div>
            </v-alert>
          </div>

          <div v-else class="no-print controls-bar">
            <v-card outlined class="pa-4">
              <v-row align="center" dense>
                <v-col cols="12" sm="5">
                  <div class="d-flex align-center gap-2">
                    <v-menu
                      v-model="fromDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formattedPeriodFrom"
                          label="Invoice From"
                          prepend-icon="mdi-calendar-start"
                          readonly
                          outlined
                          dense
                          hide-details
                          v-bind="attrs"
                          v-on="on"
                          style="min-width: 180px;"
                        />
                      </template>
                      <v-date-picker
                        v-model="periodFrom"
                        :max="periodTo"
                        @input="fromDateMenu = false; onPeriodChange()"
                      />
                    </v-menu>

                    <v-menu
                      v-model="toDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formattedPeriodTo"
                          label="Invoice To"
                          prepend-icon="mdi-calendar-end"
                          readonly
                          outlined
                          dense
                          hide-details
                          v-bind="attrs"
                          v-on="on"
                          style="min-width: 180px;"
                        />
                      </template>
                      <v-date-picker
                        v-model="periodTo"
                        :min="periodFrom"
                        @input="toDateMenu = false; onPeriodChange()"
                      />
                    </v-menu>
                  </div>

                  <div class="d-flex align-center mt-1">
                    <v-switch
                      v-model="autoPeriod"
                      label="Auto — from last tenant invoice"
                      dense
                      hide-details
                      class="mt-0"
                      color="primary"
                      @change="onAutoPeriodChange"
                    />
                    <v-chip v-if="autoPeriod" x-small color="primary" dark class="ml-2">
                      {{ autoPeriodHint }}
                    </v-chip>
                  </div>

                  <div v-if="autoPeriod" class="text-caption grey--text mt-1">
                    <v-icon x-small>mdi-information-outline</v-icon>
                    Each lease uses its last <strong>tenant invoice</strong> date from the
                    <code>Invoices</code> collection. Window ends
                    <strong>1st of the following month</strong> when showing zeros.
                  </div>
                </v-col>

                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model.number="commissionPercent"
                    label="Commission %"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    suffix="%"
                    outlined
                    dense
                    hide-details
                    prepend-icon="mdi-percent"
                  />
                </v-col>

                <v-col cols="12" sm="3">
                  <v-btn
                    block
                    color="primary"
                    :loading="generating"
                    @click="generateInvoice"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    Generate
                  </v-btn>
                </v-col>

                <v-col cols="12" sm="2" class="text-right">
                  <v-btn text small @click="$router.back()">
                    <v-icon left small>mdi-arrow-left</v-icon> Back
                  </v-btn>
                </v-col>
              </v-row>

              <v-row dense class="mt-2" align="center">
                <v-col cols="12" sm="8">
                  <v-switch
                    v-model="showOnlyPositive"
                    :label="showOnlyPositive
                      ? 'Showing only lines with a total'
                      : 'Showing all lines (incl. R0.00) — window extends to 1st of next month'"
                    dense
                    hide-details
                    color="primary"
                    class="mt-0"
                    @change="onToggleChange"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="text-right text-caption grey--text">
                  Total: <strong>{{ items.length }}</strong>
                  · Positive: <strong>{{ positiveCount }}</strong>
                  · Zero: <strong>{{ zeroCount }}</strong>
                </v-col>
              </v-row>

              <v-row dense class="mt-3">
                <v-col cols="12" sm="6">
                  <v-btn
                    block
                    class="golden-btn"
                    :disabled="displayItems.length === 0"
                    @click="printInvoice"
                  >
                    <v-icon left small>mdi-printer</v-icon> Print Invoice
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-btn
                    block
                    class="golden-btn"
                    :disabled="displayItems.length === 0"
                    @click="openEmailDialog"
                  >
                    <v-icon left small>mdi-email-send</v-icon> Send by Email
                  </v-btn>
                </v-col>
              </v-row>

              <div class="mt-2 text-caption grey--text">
                Properties: <strong>{{ properties.length }}</strong>
                · Leases: <strong>{{ leaseWindows.length }}</strong>
                · Lines: <strong>{{ items.length }}</strong>
                · Period: <strong>{{ formattedPeriodFrom }} → {{ formattedPeriodTo }}</strong>
                · Seq: <strong>{{ String(invoiceSequence).padStart(3, "0") }}</strong>
                · Overrides: <strong>{{ overrideCount }}</strong>
              </div>

              <v-expansion-panels v-if="autoPeriod && leaseWindows.length" flat class="mt-3">
                <v-expansion-panel>
                  <v-expansion-panel-header class="text-caption">
                    <span>
                      <v-icon small left>mdi-format-list-bulleted</v-icon>
                      Per-lease invoice windows ({{ leaseWindows.length }})
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th class="text-left">Lease</th>
                          <th class="text-left">Tenant</th>
                          <th class="text-left">From</th>
                          <th class="text-left">Zero-up-to</th>
                          <th class="text-left">Last tenant invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="lw in leaseWindows" :key="lw.leaseRef">
                          <td>{{ lw.leaseRef }}</td>
                          <td>{{ lw.tenantName }}</td>
                          <td>{{ formatDateForPicker(lw.from) }}</td>
                          <td>{{ formatDateForPicker(lw.zeroTo) }}</td>
                          <td>{{ lw.lastTenantInvoiceAt ? formatDateForPicker(lw.lastTenantInvoiceAt) : '—' }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>
          </div>

          <div ref="printArea" class="invoice-page" id="invoice-print-area">
            <header class="header">
              <div class="company-info">
                <h1 class="company-name">GQ Property Group</h1>
                <p class="address">28A Park Drive</p>
                <p class="address">Central</p>
                <p class="address">Gqeberha</p>
              </div>
              <div class="logo">
                <img src="@/assets/circle.png" alt="GQ Property Group" class="logo-img" />
              </div>
            </header>

            <div class="accent-line"></div>

            <section class="meta-section">
              <div class="date-block">
                <h3 class="meta-label">Date</h3>
                <p class="meta-value">{{ formattedDate }}</p>
              </div>
              <div class="to-block">
                <h3 class="meta-label">To</h3>
                <p class="meta-value landlord-name">{{ landlordDisplayName }}</p>
                <p
                  v-for="(line, i) in landlordAddressLines"
                  :key="'addr-' + i"
                  class="meta-value address-line"
                >{{ line }}</p>
              </div>
              <div class="invoice-no-block">
                <h3 class="meta-label">Invoice No</h3>
                <p class="meta-value invoice-number-inline">
                  <strong>{{ invoiceNumber }}</strong>
                </p>
              </div>
            </section>

            <div class="divider"></div>

            <table class="invoice-table">
              <thead>
                <tr>
                  <th class="col-qty">Quantity</th>
                  <th class="col-desc">Description</th>
                  <th class="col-price">Unit Price</th>
                  <th class="col-total">Total</th>
                  <th class="col-actions no-print">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="displayItems.length === 0">
                  <td colspan="5" class="empty-row">
                    No un-invoiced rent allocations in this period.
                  </td>
                </tr>
                <tr
                  v-for="row in displayItems"
                  :key="row._key"
                  :class="{
                    'row-alt': row._displayIndex % 2 === 1,
                    'row-zero': row._isZero,
                  }"
                >
                  <td class="col-qty qty-cell">
                    <span>{{ row.percent }}%</span>
                    <v-chip
                      v-if="row.customPercent"
                      x-small
                      color="orange"
                      dark
                      class="ml-2 custom-chip no-print"
                      @click="openPercentDialog(row._index)"
                      style="cursor: pointer;"
                    >custom</v-chip>
                    <v-chip
                      v-if="row.customAmount"
                      x-small
                      color="deep-orange"
                      dark
                      class="ml-1 custom-chip no-print"
                    >edited</v-chip>
                    <v-btn
                      icon
                      x-small
                      class="ml-1 percent-edit-btn no-print"
                      @click="openAmountDialog(row._index)"
                      title="Edit line amount"
                    >
                      <v-icon x-small color="primary">mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                  <td class="col-desc">{{ row.description }}</td>
                  <td class="col-price">{{ formatCurrency(row.unitPrice) }}</td>
                  <td class="col-total">{{ formatCurrency(row.total) }}</td>
                  <td class="text-center no-print actions-cell">
                    <v-btn icon x-small @click="openPercentDialog(row._index)" title="Override %">
                      <v-icon x-small color="grey darken-2">mdi-percent</v-icon>
                    </v-btn>
                    <v-btn icon x-small @click="confirmDeleteItem(row._index)" title="Remove from invoice">
                      <v-icon x-small color="red">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="total-row">
              <span class="total-label">Total Due</span>
              <span class="total-amount">{{ formatCurrency(totalDue) }}</span>
            </div>

            <section class="footer-section">
              <div class="bank-details">
                <h4>Bank Details:</h4>
                <p>GQ Property Group</p>
                <p>FNB Cheque</p>
                <p>Branch - 250655</p>
                <p>Account - 63123934283</p>
              </div>
              <div class="thank-you">Thank you for your business!</div>
            </section>

            <footer class="contact-footer">
              <span>Tel: +27 (0) 76 306 4601</span>
              <span>Email: mark@gqproperty.co.za</span>
            </footer>
          </div>
        </v-tab-item>

        <!-- ============================================================
             TAB 2 — HISTORY
             ============================================================ -->
        <v-tab-item>
          <div class="controls-bar">
            <v-card outlined class="pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold mb-0">
                  <v-icon left small color="grey darken-2">mdi-history</v-icon>
                  Invoice Send History — {{ landlordDisplayName }}
                </h3>
                <v-btn text x-small :loading="loadingHistory" @click="loadSendHistory">
                  <v-icon left x-small>mdi-refresh</v-icon> Refresh
                </v-btn>
              </div>

              <div v-if="sendHistory.length === 0" class="text-center py-8 grey--text">
                <v-icon large color="grey lighten-1">mdi-email-off-outline</v-icon>
                <p class="mt-3">No invoices have been sent yet for this landlord.</p>
              </div>

              <v-simple-table v-else dense>
                <thead>
                  <tr>
                    <th class="text-left">Sent At</th>
                    <th class="text-left">Invoice #</th>
                    <th class="text-center">Seq</th>
                    <th class="text-left">Period</th>
                    <th class="text-left">Sent To</th>
                    <th class="text-left">Sent By</th>
                    <th class="text-right">Items</th>
                    <th class="text-right">Total</th>
                    <th class="text-center">Status</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="h in sendHistory" :key="h.id">
                    <td>{{ formatDateTime(h.sentAt) }}</td>
                    <td>{{ h.invoiceNumber }}</td>
                    <td class="text-center">
                      {{ h.sequence ? String(h.sequence).padStart(3, "0") : '—' }}
                    </td>
                    <td>{{ historyPeriodLabel(h) }}</td>
                    <td>{{ h.to }}</td>
                    <td>{{ h.sentBy || '—' }}</td>
                    <td class="text-right">{{ h.itemCount || 0 }}</td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(h.total) }}
                    </td>
                    <td class="text-center">
                      <v-chip
                        x-small
                        :color="h.status === 'sent' ? 'green' : h.status === 'pending' ? 'orange' : 'red'"
                        dark
                      >
                        {{ h.status }}
                      </v-chip>
                    </td>
                    <td class="text-center actions-cell">
                      <v-btn icon small @click="openHistoryEntry(h)" title="View as sent">
                        <v-icon small color="grey darken-2">mdi-eye</v-icon>
                      </v-btn>
                      <v-btn icon small @click="resendHistoryEntry(h)" title="Resend">
                        <v-icon small color="primary">mdi-send</v-icon>
                      </v-btn>
                      <v-btn icon small @click="confirmDeleteHistory(h)" title="Delete">
                        <v-icon small color="red">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </div>
        </v-tab-item>

        <!-- ============================================================
             TAB 3 — STATEMENT
             ============================================================ -->
        <v-tab-item>
          <div class="controls-bar">
            <v-card outlined class="pa-4 mb-4">
              <v-row align="center" dense>
                <v-col cols="12" sm="3">
                  <v-menu
                    v-model="statementStartMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="statementStartDate"
                        label="Statement From"
                        prepend-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="statementStartDate"
                      type="month"
                      @input="statementStartMenu = false"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12" sm="3">
                  <v-menu
                    v-model="statementEndMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="statementEndDate"
                        label="Statement To"
                        prepend-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="statementEndDate"
                      type="month"
                      @input="statementEndMenu = false"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12" sm="6" class="text-right">
                  <v-btn class="golden-btn mr-2" :loading="loadingStatement" @click="loadStatement">
                    <v-icon left small>mdi-receipt</v-icon>
                    Build Statement
                  </v-btn>
                  <v-btn text small :disabled="statementRows.length === 0" @click="printStatement">
                    <v-icon left small>mdi-printer</v-icon> Print
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <div v-if="statementRows.length > 0" id="statement-print-area" class="statement-page">
              <div class="statement-header">
                <div>
                  <h1 class="company-name">GQ Property Group</h1>
                  <p class="address">28A Park Drive</p>
                  <p class="address">Central</p>
                  <p class="address">Gqeberha</p>
                </div>
                <div class="logo">
                  <img src="@/assets/circle.png" alt="GQ Property Group" class="logo-img" />
                </div>
              </div>

              <div class="accent-line"></div>

              <div class="statement-meta">
                <div>
                  <h3 class="meta-label">Statement For</h3>
                  <p class="meta-value landlord-name">{{ landlordDisplayName }}</p>
                  <p
                    v-for="(line, i) in landlordAddressLines"
                    :key="'stmt-addr-' + i"
                    class="meta-value address-line"
                  >{{ line }}</p>
                </div>
                <div>
                  <h3 class="meta-label">Statement Period</h3>
                  <p class="meta-value">{{ formatPeriodLabel(statementStartDate) }}</p>
                  <p class="meta-value">to {{ formatPeriodLabel(statementEndDate) }}</p>
                  <p class="meta-value" style="margin-top: 8px;">
                    <strong>Total Invoices:</strong> {{ statementRows.length }}
                  </p>
                </div>
              </div>

              <table class="statement-table">
                <thead>
                  <tr>
                    <th class="col-date">Date Sent</th>
                    <th class="col-inv">Invoice #</th>
                    <th class="col-month">Period</th>
                    <th class="col-desc">Items</th>
                    <th class="col-total">Amount</th>
                    <th class="col-status">Status</th>
                    <th class="col-balance">Running Total</th>
                    <th class="col-actions no-print">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in statementRows"
                    :key="row.id"
                    :class="{ 'row-alt': idx % 2 === 1 }"
                  >
                    <td>{{ row.dateSent }}</td>
                    <td>{{ row.invoiceNumber }}</td>
                    <td>{{ row.monthsLabel }}</td>
                    <td>{{ row.itemCount }} item(s)</td>
                    <td class="text-right">{{ formatCurrency(row.total) }}</td>
                    <td>
                      <span
                        :class="{
                          'status-sent': row.status === 'sent',
                          'status-failed': row.status === 'failed',
                          'status-pending': row.status === 'pending',
                        }"
                      >{{ row.status }}</span>
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(row.runningTotal) }}
                    </td>
                    <td class="text-center no-print actions-cell">
                      <v-btn icon small @click="confirmDeleteStatement(row)">
                        <v-icon small color="red">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="statement-total-row">
                    <td colspan="4" class="text-right">TOTAL INVOICED</td>
                    <td class="text-right">{{ formatCurrency(statementTotal) }}</td>
                    <td colspan="3"></td>
                  </tr>
                </tfoot>
              </table>

              <div class="statement-footer">
                <p><strong>Note:</strong> This statement reflects invoices sent via the GQ Property Group system for the period shown.</p>
                <p class="contact-line">
                  <span>Tel: +27 (0) 76 306 4601</span>
                  <span>Email: mark@gqproperty.co.za</span>
                </p>
              </div>
            </div>

            <v-card v-else outlined class="pa-6 text-center">
              <v-icon large color="grey lighten-1">mdi-receipt</v-icon>
              <p class="mt-3 grey--text">
                Select a period and click <strong>Build Statement</strong>.
              </p>
            </v-card>
          </div>
        </v-tab-item>

      </v-tabs-items>

      <!-- ====== EMAIL DIALOG ====== -->
      <v-dialog v-model="emailDialog" max-width="560" persistent>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-email-send</v-icon>
            Send Invoice by Email
            <v-chip small color="primary" class="ml-2" dark>{{ invoiceNumber }}</v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-text-field v-model="emailForm.to" label="To" outlined dense prepend-inner-icon="mdi-account" hint="Landlord email (auto-filled if available)" persistent-hint class="mb-4" />
            <v-text-field v-model="emailForm.cc" label="CC (optional)" outlined dense prepend-inner-icon="mdi-account-multiple" class="mb-4" />
            <v-text-field v-model="emailForm.subject" label="Subject" outlined dense prepend-inner-icon="mdi-tag" class="mb-4" />
            <v-textarea v-model="emailForm.message" label="Message" outlined dense rows="5" prepend-inner-icon="mdi-message-text" />
            <v-alert type="info" dense outlined class="mt-3 text-caption">
              The invoice will be attached as a PDF with a unique timestamp in the filename. A copy of the send will be saved to history.
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="sending" @click="emailDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="sending" :disabled="!emailForm.to || !emailForm.subject" @click="sendEmail">
              <v-icon left small>mdi-send</v-icon>
              Send
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ====== DELETE INVOICE CONFIRM ====== -->
      <v-dialog v-model="deleteDialog" max-width="480" persistent>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold red--text">
            <v-icon left color="red">mdi-alert</v-icon>
            Delete Invoice Record?
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <p class="mb-3">You are about to permanently delete this invoice record from the history.</p>
            <v-simple-table dense v-if="invoiceToDelete">
              <tbody>
                <tr><td class="font-weight-bold">Invoice #</td><td>{{ invoiceToDelete.invoiceNumber || '—' }}</td></tr>
                <tr><td class="font-weight-bold">Period</td><td>{{ historyPeriodLabel(invoiceToDelete) }}</td></tr>
                <tr><td class="font-weight-bold">Sent To</td><td>{{ invoiceToDelete.to || '—' }}</td></tr>
                <tr><td class="font-weight-bold">Total</td><td>{{ formatCurrency(invoiceToDelete.total || 0) }}</td></tr>
              </tbody>
            </v-simple-table>
            <v-alert type="warning" dense outlined class="mt-4 text-caption">
              This action <strong>cannot be undone</strong>.
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="deleting" @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="red" dark :loading="deleting" @click="executeDelete">
              <v-icon left small>mdi-delete</v-icon>
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ====== DELETE LINE FROM INVOICE ====== -->
      <v-dialog v-model="deleteItemDialog" max-width="440" persistent>
        <v-card v-if="deleteItemForm.index >= 0 && items[deleteItemForm.index]">
          <v-card-title class="text-subtitle-1 font-weight-bold red--text">
            <v-icon left color="red">mdi-alert</v-icon>
            Remove Line From Invoice?
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <p class="mb-3">
              This removes the line from the current invoice only. The underlying transaction is not affected.
            </p>
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td class="font-weight-bold">Lease</td>
                  <td>{{ items[deleteItemForm.index].leaseRef }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Month</td>
                  <td>{{ items[deleteItemForm.index].monthKey }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Description</td>
                  <td>{{ items[deleteItemForm.index].description }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Total</td>
                  <td>{{ formatCurrency(items[deleteItemForm.index].total) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteItemDialog = false">Cancel</v-btn>
            <v-btn color="red" dark @click="deleteItem">
              <v-icon left small>mdi-delete</v-icon>
              Remove
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ====== AMOUNT OVERRIDE ====== -->
      <v-dialog v-model="amountDialog" max-width="520" persistent>
        <v-card v-if="amountForm.itemIndex >= 0 && items[amountForm.itemIndex]">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-pencil</v-icon>
            Edit Line Amount
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <p class="text-caption grey--text mb-3">
              {{ items[amountForm.itemIndex].description }}
            </p>
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr>
                  <td class="font-weight-bold">Rent received (from allocations)</td>
                  <td class="text-right">{{ formatCurrency(items[amountForm.itemIndex].rentPaid) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Commission %</td>
                  <td class="text-right">{{ items[amountForm.itemIndex].percent }}%</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Calculated Amount</td>
                  <td class="text-right">{{ formatCurrency(items[amountForm.itemIndex].calculatedTotal) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-text-field
              v-model.number="amountForm.amount"
              label="Line Amount (R)"
              prefix="R"
              type="number"
              step="0.01"
              outlined
              dense
              autofocus
            />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-btn v-if="items[amountForm.itemIndex]?.customAmount" text color="grey darken-2" @click="resetItemAmount(amountForm.itemIndex)">
              Reset to calculated
            </v-btn>
            <v-spacer />
            <v-btn text @click="amountDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="saveAmountOverride">
              <v-icon left small>mdi-check</v-icon> Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ====== PERCENT OVERRIDE ====== -->
      <v-dialog v-model="percentDialog" max-width="480" persistent>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-percent</v-icon>
            Override Commission % for This Line
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4" v-if="percentForm.itemIndex >= 0 && items[percentForm.itemIndex]">
            <p class="text-caption grey--text mb-3">{{ items[percentForm.itemIndex].description }}</p>
            <v-text-field
              v-model.number="percentForm.percent"
              label="Commission %"
              type="number"
              min="0"
              max="100"
              step="0.1"
              suffix="%"
              outlined
              dense
              autofocus
            />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-btn
              v-if="percentForm.itemIndex >= 0 && items[percentForm.itemIndex]?.customPercent"
              text color="grey darken-2"
              @click="resetPercentOverride(percentForm.itemIndex)"
            >
              Reset to {{ commissionPercent }}%
            </v-btn>
            <v-spacer />
            <v-btn text :disabled="sending" @click="percentDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="savePercentOverride">
              <v-icon left small>mdi-check</v-icon> Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-overlay :value="sending" z-index="9999">
        <div class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" />
          <p class="white--text mt-4">{{ sendingStatus }}</p>
        </div>
      </v-overlay>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000" top>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>
    </template>
  </div>
</template>

<script>
import {
  getFirestore,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  addDoc,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";
import { logEvent } from "@/utils/logEvent";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const db = getFirestore(firebaseApp);

const EMAIL_ENDPOINT = "https://sendemail-bqr766ca3q-uc.a.run.app";
const HISTORY_COLLECTION = "landlordInvoiceHistory";
const TENANT_INVOICES_COLLECTION = "Invoices";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const RENT_REGEX = /rent|monthly rent/i;

export default {
  name: "LandlordInvoice",

  data() {
    const now = new Date();
    const sixAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);

    const toIsoMonth = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

    const todayIso = now.toISOString().split("T")[0];
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split("T")[0];

    return {
      activeTab: 0,

      loading: true,
      generating: false,
      sending: false,
      sendingStatus: "",
      loadingHistory: false,

      landlord: null,
      properties: [],
      items: [],

      periodFrom: firstOfMonth,
      periodTo: todayIso,
      fromDateMenu: false,
      toDateMenu: false,
      autoPeriod: true,
      lastInvoiceSentAt: null,

      leaseWindows: [],

      showOnlyPositive: true,

      viewingHistoryInvoice: false,
      viewingHistoryId: null,

      commissionPercent: 10,
      invoiceNumber: "",
      invoiceSequence: 1,

      emailDialog: false,
      emailForm: { to: "", cc: "", subject: "", message: "" },

      sendHistory: [],

      snackbar: { show: false, text: "", color: "success" },

      deleteDialog: false,
      deleting: false,
      invoiceToDelete: null,

      // Delete item from current invoice
      deleteItemDialog: false,
      deleteItemForm: { index: -1 },

      percentDialog: false,
      percentForm: {
        itemIndex: -1,
        percent: 0,
        custom: false,
      },

      amountDialog: false,
      amountForm: {
        itemIndex: -1,
        amount: 0,
      },

      statementStartDate: toIsoMonth(sixAgo),
      statementEndDate: toIsoMonth(now),
      statementStartMenu: false,
      statementEndMenu: false,
      statementRows: [],
      loadingStatement: false,

      _chargeCache: [],
    };
  },

  computed: {
    formattedDate() {
      return new Date().toLocaleDateString("en-ZA", {
        year: "numeric", month: "long", day: "numeric",
      });
    },

    formattedPeriodFrom() {
      if (this.autoPeriod && this.leaseWindows.length > 0) {
        const froms = this.leaseWindows.map((lw) => lw.from).filter(Boolean).sort();
        if (froms.length > 0) return this.formatDateForPicker(froms[0]);
      }
      return this.formatDateForPicker(this.periodFrom);
    },

    formattedPeriodTo() {
      const iso = this.showOnlyPositive
        ? this.periodTo
        : this.endOfCurrentMonthPlusOneDay();
      return this.formatDateForPicker(iso);
    },

    selectedMonthsLabel() {
      const toIso = this.showOnlyPositive
        ? this.periodTo
        : this.endOfCurrentMonthPlusOneDay();
      return `${this.formattedPeriodFrom} → ${this.formatDateForPicker(toIso)}`;
    },

    autoPeriodHint() {
      if (this.leaseWindows.length > 0) {
        return `${this.leaseWindows.length} lease(s) with invoices`;
      }
      return "No leases with invoices";
    },

    positiveCount() {
      return this.items.filter((i) => Number(i.total) > 0.01).length;
    },

    zeroCount() {
      return this.items.filter((i) => Number(i.total) <= 0.01).length;
    },

    displayItems() {
      const indexed = this.items.map((it, idx) => ({
        ...it,
        _index: idx,
        _isZero: Number(it.total) <= 0.01,
        _key: `${it.leaseRef}||${it.monthKey}||${idx}`,
      }));

      const filtered = this.showOnlyPositive
        ? indexed.filter((it) => !it._isZero)
        : indexed;

      return filtered.map((it, di) => ({ ...it, _displayIndex: di }));
    },

    totalDue() {
      return this.items.reduce((sum, item) => sum + (item.total || 0), 0);
    },

    overrideCount() {
      return this.items.filter((i) => i.customPercent || i.customAmount).length;
    },

    landlordDisplayName() {
      if (!this.landlord) return "";
      return (
        this.landlord.displayAs || this.landlord.name ||
        this.landlord.displayName || this.landlord.fullName ||
        "Unnamed Landlord"
      );
    },

    landlordAddressLines() {
      const l = this.landlord;
      if (!l) return [];
      const lines = [];

      if (l.address && typeof l.address === "string") {
        l.address.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean).forEach((s) => lines.push(s));
        return lines;
      }
      if (l.address && typeof l.address === "object") {
        const a = l.address;
        [a.line1, a.line2, a.line3, a.street, a.suburb, a.city,
         a.province, a.postalCode, a.country].filter(Boolean).forEach((s) => lines.push(s));
        return lines;
      }
      [l.addressLine1, l.addressLine2, l.addressLine3, l.street,
       l.suburb, l.city, l.province, l.postalCode, l.country]
        .filter(Boolean).forEach((s) => lines.push(s));
      if (lines.length === 0 && l.physicalAddress) lines.push(l.physicalAddress);
      return lines;
    },

    statementTotal() {
      return this.statementRows.reduce((s, r) => s + (r.total || 0), 0);
    },
  },

  watch: {
    commissionPercent(newVal, oldVal) {
      if (Number(newVal) === Number(oldVal)) return;
      if (!this.landlord) return;
      if (this.viewingHistoryInvoice) return;
      this.rebuildItemsFromCache();
    },
  },

  async mounted() {
    await this.loadLandlord();
    await this.loadInvoicePeriod();
    await this.generateInvoice();
    await this.loadSendHistory();
    this.loading = false;
  },

  methods: {
    // ============================================================
    //  DATE HELPERS
    // ============================================================
    formatDateForPicker(isoDate) {
      if (!isoDate) return "—";
      const d = new Date(isoDate + "T00:00:00");
      if (isNaN(d.getTime())) return isoDate;
      return d.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "2-digit" });
    },

    monthKeyFromIso(isoDate) {
      const d = isoDate instanceof Date ? isoDate : new Date(isoDate + "T00:00:00");
      if (isNaN(d.getTime())) return "";
      return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    },

    isoMonthFromKey(monthKey) {
      if (!monthKey) return "";
      const [name, year] = monthKey.split(" ");
      const idx = MONTH_NAMES.indexOf(name);
      if (idx < 0) return "";
      return `${year}-${String(idx + 1).padStart(2, "0")}`;
    },

    endOfCurrentMonthPlusOneDay() {
      const now = new Date();
      const eom = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      eom.setDate(eom.getDate() + 1);
      const y = eom.getFullYear();
      const m = String(eom.getMonth() + 1).padStart(2, "0");
      const d = String(eom.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    firstOfNextMonthFrom(isoDate) {
      if (!isoDate) return null;
      const d = new Date(isoDate + "T00:00:00");
      if (isNaN(d.getTime())) return null;
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const y = next.getFullYear();
      const m = String(next.getMonth() + 1).padStart(2, "0");
      const day = String(next.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    toLocalIso(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    // ============================================================
    //  PERIOD DETECTION
    // ============================================================
    async loadInvoicePeriod() {
      try {
        const leases = await this.collectLeaseRefs();
        const lastByLease = new Map();

        for (const lease of leases) {
          const leaseRef = lease.leaseReference;
          let isoDate = null;

          try {
            const invoicesSnap = await getDocs(
              query(
                collection(db, TENANT_INVOICES_COLLECTION),
                where("leaseReference", "==", leaseRef),
                orderBy("createdAt", "desc"),
                limit(1)
              )
            );
            if (!invoicesSnap.empty) {
              isoDate = this.extractInvoiceDate(invoicesSnap.docs[0].data());
            }
          } catch (e) {
            console.warn(`[Invoices] orderBy query failed for ${leaseRef}:`, e.message);
          }

          if (!isoDate) {
            try {
              const invoicesSnap = await getDocs(
                query(
                  collection(db, TENANT_INVOICES_COLLECTION),
                  where("leaseReference", "==", leaseRef)
                )
              );
              let newest = null;
              for (const d of invoicesSnap.docs) {
                const extracted = this.extractInvoiceDate(d.data());
                if (extracted && (!newest || extracted > newest)) {
                  newest = extracted;
                }
              }
              isoDate = newest;
            } catch (e2) {
              console.warn(`[Invoices] fallback query failed for ${leaseRef}:`, e2.message);
            }
          }

          if (isoDate) lastByLease.set(leaseRef, isoDate);
        }

        let landlordLast = null;
        for (const iso of lastByLease.values()) {
          if (!landlordLast || iso > landlordLast) landlordLast = iso;
        }
        this.lastInvoiceSentAt = landlordLast;

        this.leaseWindows = leases
          .map((l) => {
            const lastIso = lastByLease.get(l.leaseReference) || null;
            if (!lastIso) return null;

            return {
              leaseRef: l.leaseReference,
              tenantName: l.tenantName,
              from: lastIso,
              zeroTo: this.firstOfNextMonthFrom(lastIso),
              lastTenantInvoiceAt: lastIso,
            };
          })
          .filter(Boolean);

        const now = new Date();
        if (this.autoPeriod) {
          const froms = this.leaseWindows.map((lw) => lw.from).filter(Boolean).sort();
          if (froms.length > 0) this.periodFrom = froms[0];
          this.periodTo = this.toLocalIso(now);
        }
      } catch (e) {
        console.error("loadInvoicePeriod failed:", e);
      }
    },

    extractInvoiceDate(h) {
      if (!h) return null;

      const fromDateObj = (dt) => {
        if (!dt || isNaN(dt.getTime())) return null;
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const d = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };

      if (h.createdAt) {
        if (typeof h.createdAt.toDate === "function") {
          return fromDateObj(h.createdAt.toDate());
        }
        if (typeof h.createdAt === "string") return h.createdAt.split("T")[0];
        if (typeof h.createdAt === "number") {
          return fromDateObj(new Date(h.createdAt));
        }
      }
      if (h.datePaid) return String(h.datePaid).split("T")[0];
      return null;
    },

    applyAutoPeriod() {
      if (this.leaseWindows.length > 0) {
        const froms = this.leaseWindows.map((lw) => lw.from).filter(Boolean).sort();
        if (froms.length > 0) this.periodFrom = froms[0];
      }
      this.periodTo = this.toLocalIso(new Date());
    },

    onAutoPeriodChange() {
      if (this.autoPeriod) {
        this.applyAutoPeriod();
        this.generateInvoice();
      }
    },

    onPeriodChange() {
      this.autoPeriod = false;
      this.generateInvoice();
    },

    onToggleChange() {
      if (this.viewingHistoryInvoice) return;
      this.generateInvoice();
    },

    // ============================================================
    //  LEASES
    // ============================================================
    async collectLeaseRefs() {
      if (this.properties.length === 0) await this.loadProperties();
      const refs = [];
      for (const prop of this.properties) {
        const leasesSnap = await getDocs(
          query(collection(db, "Leases"), where("propertyId", "==", prop.id))
        );
        leasesSnap.docs.forEach((d) => {
          const ld = d.data();

          if (ld.deleted === true) return;
          if (ld.isDeleted === true) return;
          if (ld.deletedAt) return;

          if (typeof ld.status === "string") {
            const s = ld.status.toLowerCase();
            if (
              s === "deleted" ||
              s === "inactive" ||
              s === "terminated" ||
              s === "cancelled" ||
              s === "canceled"
            ) {
              return;
            }
          }

          if (ld.active === false) return;
          if (ld.isActive === false) return;

          if (ld.leaseReference) {
            refs.push({
              leaseReference: ld.leaseReference,
              tenantName: ld.tenantName || "Unknown Tenant",
              propertyId: prop.id,
            });
          }
        });
      }
      return refs;
    },

    // ============================================================
    //  PAYMENTS + RENT ALLOCATIONS
    // ============================================================
    async collectPaymentAllocations(leases) {
      const payments = [];

      for (const lease of leases) {
        const txSnap = await getDocs(
          query(
            collection(db, "Transactions"),
            where("leaseReference", "==", lease.leaseReference)
          )
        );

        const docs = txSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const paymentDocs = docs.filter(
          (t) =>
            (t.type === "payment" || Number(t.amountPaid) < 0) &&
            t.type !== "credit" &&
            t.type !== "invoice"
        );
        const chargeDocs = docs.filter((t) => Number(t.amountPaid) > 0);

        const norm = (s) =>
          typeof s === "string" ? s.replace(/\.+$/, "").trim() : "";

        const chargeById = new Map();
        chargeDocs.forEach((c) => {
          const desc = String(c.description || "");
          const ctype = String(c.chargeType || c.transactionType || "");
          const isRent = RENT_REGEX.test(desc) || RENT_REGEX.test(ctype);
          chargeById.set(c.id, { isRent, charge: c });
        });

        paymentDocs.forEach((p) => {
          const payId = norm(p.id);
          const payRef = norm(p.reference);
          const paymentIdentities = [payId, payRef].filter((x) => x.length > 3);
          if (paymentIdentities.length === 0) return;

          const detailByCharge = new Map();
          const matchesIdentity = (s) => {
            const n = norm(s);
            if (n.length <= 3) return false;
            return paymentIdentities.some(
              (pid) => pid === n || pid.includes(n) || n.includes(pid)
            );
          };

          chargeDocs.forEach((c) => {
            const meta = chargeById.get(c.id);
            if (!meta || !meta.isRent) return;

            const allocations = Array.isArray(c.allocations) ? c.allocations : [];
            allocations.forEach((a) => {
              const candidates = [a.paymentId, a.paymentRef, a.ref]
                .filter((x) => typeof x === "string");
              if (!candidates.some((cand) => matchesIdentity(cand))) return;

              const amt = Number(a.amount) || 0;
              if (amt <= 0.01) return;

              const prev = detailByCharge.get(c.id) || 0;
              detailByCharge.set(c.id, prev + amt);
            });
          });

          const ownAllocs = [];
          if (Array.isArray(p.allocation)) ownAllocs.push(...p.allocation);
          if (Array.isArray(p.allocations)) ownAllocs.push(...p.allocations);
          if (Array.isArray(p.items)) ownAllocs.push(...p.items);

          ownAllocs.forEach((entry) => {
            const amt = Number(entry.amount) || 0;
            if (amt <= 0.01) return;

            const entryChargeId = entry.transactionId || entry.chargeId;
            if (entryChargeId) {
              const meta = chargeById.get(entryChargeId);
              if (!meta || !meta.isRent) return;
              const already = detailByCharge.get(entryChargeId) || 0;
              if (amt > already) detailByCharge.set(entryChargeId, amt);
              return;
            }

            const descText = String(entry.description || "").toLowerCase();
            if (!RENT_REGEX.test(descText)) return;

            for (const [cid, meta] of chargeById) {
              if (!meta.isRent) continue;
              if (detailByCharge.has(cid)) continue;
              detailByCharge.set(cid, amt);
              break;
            }
          });

          let rentAllocated = 0;
          const matchedDetail = [];
          detailByCharge.forEach((amt, chargeId) => {
            rentAllocated += amt;
            const meta = chargeById.get(chargeId);
            matchedDetail.push({
              chargeId,
              chargeDesc: (meta && (meta.charge.description || meta.charge.chargeType)) || "(no desc)",
              chargeMonthKey: meta ? this.monthKeyFromIso(meta.charge.datePaid) : "",
              amount: amt,
            });
          });

          if (rentAllocated > 0.01) {
            payments.push({
              id: p.id,
              reference: p.reference || p.id,
              leaseRef: lease.leaseReference,
              tenantName: lease.tenantName,
              datePaid: p.datePaid,
              monthKey: this.monthKeyFromIso(p.datePaid),
              rentAllocated: Number(rentAllocated.toFixed(2)),
              matchedDetail,
            });
          }
        });
      }

      return payments;
    },

    // ============================================================
    //  INVOICE GENERATION
    // ============================================================
    async generateInvoice() {
      if (!this.landlord) return;
      this.generating = true;
      this.items = [];

      try {
        if (this.leaseWindows.length === 0) {
          await this.loadInvoicePeriod();
        }

        let leases = await this.collectLeaseRefs();

        const allowedLeaseRefs = new Set(this.leaseWindows.map((lw) => lw.leaseRef));
        leases = leases.filter((l) => allowedLeaseRefs.has(l.leaseReference));

        if (leases.length === 0) {
          this._chargeCache = [];
          this.generating = false;
          return;
        }

        const windowByLease = new Map();
        this.leaseWindows.forEach((lw) => windowByLease.set(lw.leaseRef, lw));

        const globalFrom = this.periodFrom;
        const globalTo = this.periodTo;

        const payments = await this.collectPaymentAllocations(leases);

        const invoicedPaymentIds = new Set();
        const invoicedRefs = new Set();
        try {
          const histSnap = await getDocs(
            query(
              collection(db, HISTORY_COLLECTION),
              where("landlordId", "==", this.landlord.id)
            )
          );
          histSnap.docs.forEach((d) => {
            const h = d.data();
            if (h.status !== "sent") return;
            (h.invoicedPaymentIds || []).forEach((r) => invoicedPaymentIds.add(r));
            (h.invoicedPaymentRefs || []).forEach((r) => invoicedRefs.add(r));
          });
        } catch (e) {
          console.warn("Could not load history for exclusion:", e);
        }

        const grouped = new Map();
        const usedPaymentIds = new Set();
        const usedPaymentRefs = new Set();

        payments.forEach((p) => {
          if (invoicedPaymentIds.has(p.id)) return;
          if (p.reference && invoicedRefs.has(p.reference)) return;
          if (!p.datePaid) return;

          let fromIso = globalFrom;
          let toIso = globalTo;

          if (this.autoPeriod && windowByLease.has(p.leaseRef)) {
            const w = windowByLease.get(p.leaseRef);
            fromIso = w.from;
            toIso = globalTo;
          }

          if (fromIso && p.datePaid < fromIso) return;
          if (toIso && p.datePaid > toIso) return;

          const monthKey = p.monthKey || this.monthKeyFromIso(p.datePaid);
          const key = `${p.leaseRef}||${monthKey}`;
          if (!grouped.has(key)) {
            grouped.set(key, {
              leaseRef: p.leaseRef,
              tenantName: p.tenantName,
              monthKey,
              rentPaid: 0,
            });
          }
          grouped.get(key).rentPaid += p.rentAllocated;
          usedPaymentIds.add(p.id);
          if (p.reference) usedPaymentRefs.add(p.reference);
        });

        const percent = Number(this.commissionPercent) || 0;
        const positiveLines = [];

        grouped.forEach((g) => {
          const commissionAmount = (g.rentPaid * percent) / 100;
          if (commissionAmount <= 0.01) return;

          positiveLines.push({
            leaseRef: g.leaseRef,
            monthKey: g.monthKey,
            tenantName: g.tenantName,
            description: `${g.leaseRef} – ${g.monthKey} – (${this.formatCurrency(g.rentPaid)})`,
            percent,
            rentPaid: Number(g.rentPaid.toFixed(2)),
            unitPrice: Number(commissionAmount.toFixed(2)),
            total: Number(commissionAmount.toFixed(2)),
            calculatedTotal: Number(commissionAmount.toFixed(2)),
            customPercent: false,
            customAmount: false,
          });
        });

        const zeroLines = [];

        if (!this.showOnlyPositive) {
          const zeroGrouped = new Map();

          for (const lease of leases) {
            const txSnap = await getDocs(
              query(
                collection(db, "Transactions"),
                where("leaseReference", "==", lease.leaseReference)
              )
            );

            let w = windowByLease.get(lease.leaseReference);
            if (!w) {
              w = { from: globalFrom, zeroTo: this.endOfCurrentMonthPlusOneDay() };
            }
            const fromIso = w.from;
            const toIso = w.zeroTo;

            txSnap.docs.forEach((d) => {
              const t = d.data();
              if (Number(t.amountPaid) <= 0) return;
              if (!t.datePaid) return;
              if (fromIso && t.datePaid < fromIso) return;
              if (toIso && t.datePaid > toIso) return;

              const desc = String(t.description || "");
              const ctype = String(t.chargeType || t.transactionType || "");
              const isRent = RENT_REGEX.test(desc) || RENT_REGEX.test(ctype);
              if (!isRent) return;

              const allocated =
                Number(t.allocatedAmount) ||
                (Array.isArray(t.allocations)
                  ? t.allocations.reduce((s, a) => s + (Number(a.amount) || 0), 0)
                  : 0);

              if (allocated > 0.01) return;

              const monthKey = this.monthKeyFromIso(t.datePaid);
              const key = `${lease.leaseReference}||${monthKey}`;

              if (!zeroGrouped.has(key)) {
                zeroGrouped.set(key, {
                  leaseRef: lease.leaseReference,
                  monthKey,
                  tenantName: lease.tenantName,
                  rentCharged: 0,
                });
              }
              zeroGrouped.get(key).rentCharged += Number(t.amountPaid) || 0;
            });
          }

          const positiveKeys = new Set(
            positiveLines.map((l) => `${l.leaseRef}||${l.monthKey}`)
          );

          zeroGrouped.forEach((z, key) => {
            if (positiveKeys.has(key)) return;

            zeroLines.push({
              leaseRef: z.leaseRef,
              monthKey: z.monthKey,
              tenantName: z.tenantName,
              description: `${z.leaseRef} – ${z.monthKey} – (invoiced, unpaid ${this.formatCurrency(z.rentCharged)})`,
              percent,
              rentPaid: 0,
              unitPrice: 0,
              total: 0,
              calculatedTotal: 0,
              customPercent: false,
              customAmount: false,
            });
          });
        }

        const lineItems = [...positiveLines, ...zeroLines];

        lineItems.sort((a, b) => {
          const [an, ay] = a.monthKey.split(" ");
          const [bn, by] = b.monthKey.split(" ");
          const ai = MONTH_NAMES.indexOf(an);
          const bi = MONTH_NAMES.indexOf(bn);
          if (ay !== by) return Number(by) - Number(ay);
          if (ai !== bi) return bi - ai;
          return a.leaseRef.localeCompare(b.leaseRef);
        });

        this.items = lineItems;
        this._chargeCache = lineItems.map((it) => ({ ...it }));
        this._usedPaymentIds = Array.from(usedPaymentIds);
        this._usedPaymentRefs = Array.from(usedPaymentRefs);
      } catch (e) {
        console.error("Invoice generation failed:", e);
      } finally {
        this.generating = false;
      }
    },

    rebuildItemsFromCache() {
      if (!this._chargeCache || this._chargeCache.length === 0) {
        this.generateInvoice();
        return;
      }
      const percent = Number(this.commissionPercent) || 0;

      this.items = this._chargeCache.map((item) => {
        if (item.customAmount) return { ...item, percent };
        const commission = (item.rentPaid * percent) / 100;
        return {
          ...item,
          percent,
          unitPrice: Number(commission.toFixed(2)),
          total: Number(commission.toFixed(2)),
          calculatedTotal: Number(commission.toFixed(2)),
        };
      });
    },

    // ============================================================
    //  LANDLORD + PROPERTIES
    // ============================================================
    async loadLandlord() {
      const id = this.$route.params.id;
      try {
        const snap = await getDoc(doc(db, "landlords", id));
        if (!snap.exists()) return;
        const data = snap.data();

        this.landlord = {
          id: snap.id,
          reference: data.reference || "N/A",
          name: data.name || "",
          displayAs: data.displayAs || "",
          displayName: data.displayName || "",
          fullName: data.fullName || "",
          email: data.email || "",
          address: data.address ?? null,
          addressLine1: data.addressLine1 || "",
          addressLine2: data.addressLine2 || "",
          addressLine3: data.addressLine3 || "",
          street: data.street || "",
          suburb: data.suburb || "",
          city: data.city || "",
          province: data.province || "",
          postalCode: data.postalCode || "",
          country: data.country || "",
          physicalAddress: data.physicalAddress || "",
        };

        this.invoiceSequence = await this.getNextInvoiceSequence();
        this.invoiceNumber = this.buildInvoiceNumber(this.invoiceSequence);

        await logEvent(
          "Landlord Invoice Viewed",
          `${this.landlordDisplayName} (${this.landlord.reference})`,
          "Landlord Management"
        );

        await this.loadProperties();
      } catch (e) {
        console.error("Error loading landlord:", e);
      }
    },

    async loadProperties() {
      const found = new Map();
      const addProp = (id, data) => {
        if (!id) return;
        if (!found.has(id)) found.set(id, { id, ...data });
      };

      if (this.landlord.reference && this.landlord.reference !== "N/A") {
        try {
          const qA = query(
            collection(db, "properties"),
            where("selectedLandlord", "==", this.landlord.reference)
          );
          const snapA = await getDocs(qA);
          snapA.docs.forEach((d) => addProp(d.id, d.data()));
        } catch (e) {
          console.log("[Match] Failed:", e.message);
        }
      }

      if (found.size === 0) {
        try {
          const snapAll = await getDocs(collection(db, "properties"));
          snapAll.docs.forEach((d) => {
            const data = d.data();
            if (data.selectedLandlord === this.landlord.reference) {
              addProp(d.id, data);
            }
          });
        } catch (e) {
          console.log("[Fallback] Failed:", e.message);
        }
      }

      this.properties = Array.from(found.values());
    },

    historyPeriodLabel(h) {
      if (!h) return "—";
      if (h.monthsLabel) return h.monthsLabel;
      if (h.periodFrom && h.periodTo) {
        return `${this.formatDateForPicker(h.periodFrom)} → ${this.formatDateForPicker(h.periodTo)}`;
      }
      if (Array.isArray(h.months) && h.months.length > 0) {
        return h.months.join(", ");
      }
      if (h.month) return h.month;
      return "—";
    },

    // ============================================================
    //  INVOICE NUMBER
    // ============================================================
    buildTimestamp() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`
      );
    },

    buildInvoiceNumber(sequence) {
      const now = new Date();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const yy = String(now.getFullYear()).slice(-2);
      const ref = this.landlord?.reference || "N/A";
      const seq = String(sequence || 1).padStart(3, "0");
      return `${mm}${yy}-${ref}-${seq}`;
    },

    async getNextInvoiceSequence() {
      if (!this.landlord) return 1;
      try {
        const q = query(
          collection(db, HISTORY_COLLECTION),
          where("landlordId", "==", this.landlord.id),
          orderBy("sentAt", "desc"),
          limit(50)
        );
        const snap = await getDocs(q);
        if (snap.empty) return 1;
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const yy = String(now.getFullYear()).slice(-2);
        const prefix = `${mm}${yy}-`;
        let maxSeq = 0;
        snap.docs.forEach((d) => {
          const h = d.data();
          if (h.invoiceNumber && h.invoiceNumber.startsWith(prefix)) {
            const seq = parseInt(h.invoiceNumber.split("-").pop(), 10);
            if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
          }
        });
        return maxSeq + 1;
      } catch (e) {
        try {
          const all = await getDocs(
            query(collection(db, HISTORY_COLLECTION), where("landlordId", "==", this.landlord.id))
          );
          return all.size + 1;
        } catch (err) {
          return 1;
        }
      }
    },

    // ============================================================
    //  OVERRIDES
    // ============================================================
    openAmountDialog(index) {
      if (index < 0 || !this.items[index]) return;
      this.amountForm = { itemIndex: index, amount: this.items[index].total };
      this.amountDialog = true;
    },

    saveAmountOverride() {
      const idx = this.amountForm.itemIndex;
      if (idx < 0 || !this.items[idx]) return;
      const newAmount = Number(this.amountForm.amount) || 0;
      const item = this.items[idx];
      const isCustom = Math.abs(newAmount - Number(item.calculatedTotal || 0)) > 0.01;

      this.items.splice(idx, 1, {
        ...item,
        total: Number(newAmount.toFixed(2)),
        customAmount: isCustom,
      });
      this.items = [...this.items];
      this.amountDialog = false;
    },

    resetItemAmount(index) {
      if (index < 0 || !this.items[index]) return;
      const item = this.items[index];
      this.items.splice(index, 1, {
        ...item,
        total: item.calculatedTotal,
        customAmount: false,
      });
      this.items = [...this.items];
      this.amountDialog = false;
    },

    openPercentDialog(index) {
      if (index < 0 || !this.items[index]) return;
      this.percentForm = {
        itemIndex: index,
        percent: this.items[index].percent,
        custom: !!this.items[index].customPercent,
      };
      this.percentDialog = true;
    },

    savePercentOverride() {
      const idx = this.percentForm.itemIndex;
      if (idx < 0 || !this.items[idx]) return;
      const newPercent = Math.max(0, Math.min(100, Number(this.percentForm.percent) || 0));
      const item = this.items[idx];
      const rentPaid = Number(item.rentPaid) || 0;
      const newUnitPrice = (rentPaid * newPercent) / 100;

      this.items.splice(idx, 1, {
        ...item,
        percent: newPercent,
        unitPrice: Number(newUnitPrice.toFixed(2)),
        total: Number(newUnitPrice.toFixed(2)),
        calculatedTotal: Number(newUnitPrice.toFixed(2)),
        customPercent: newPercent !== Number(this.commissionPercent),
        customAmount: false,
      });
      this.items = [...this.items];
      this.percentDialog = false;
    },

    resetPercentOverride(index) {
      if (index < 0 || !this.items[index]) return;
      const item = this.items[index];
      const globalPercent = Number(this.commissionPercent) || 0;
      const rentPaid = Number(item.rentPaid) || 0;
      const newUnitPrice = (rentPaid * globalPercent) / 100;

      this.items.splice(index, 1, {
        ...item,
        percent: globalPercent,
        unitPrice: Number(newUnitPrice.toFixed(2)),
        total: Number(newUnitPrice.toFixed(2)),
        calculatedTotal: Number(newUnitPrice.toFixed(2)),
        customPercent: false,
        customAmount: false,
      });
      this.items = [...this.items];
      this.percentDialog = false;
    },

    // ============================================================
    //  DELETE A LINE FROM THE INVOICE (in-memory only)
    // ============================================================
    confirmDeleteItem(index) {
      if (index < 0 || !this.items[index]) return;
      this.deleteItemForm = { index };
      this.deleteItemDialog = true;
    },

    deleteItem() {
      const idx = this.deleteItemForm.index;
      if (idx < 0 || !this.items[idx]) {
        this.deleteItemDialog = false;
        return;
      }

      const removed = this.items[idx];
      this.items.splice(idx, 1);
      this.items = [...this.items];

      // Keep _chargeCache in sync so % changes don't resurrect the row
      if (Array.isArray(this._chargeCache)) {
        this._chargeCache = this._chargeCache.filter(
          (it) =>
            !(
              it.leaseRef === removed.leaseRef &&
              it.monthKey === removed.monthKey &&
              Number(it.rentPaid) === Number(removed.rentPaid)
            )
        );
      }

      this.deleteItemDialog = false;
      this.deleteItemForm = { index: -1 };

      this.snackbar = {
        show: true,
        text: `Removed ${removed.leaseRef} – ${removed.monthKey} from invoice`,
        color: "success",
      };
    },

    // ============================================================
    //  PRINT
    // ============================================================
    printInvoice() {
      this.printElement("invoice-print-area", `Invoice ${this.invoiceNumber}`);
    },

    printStatement() {
      this.printElement("statement-print-area", `Statement - ${this.landlordDisplayName}`);
    },

    printElement(elementId, title) {
      const el = document.getElementById(elementId);
      if (!el) { window.print(); return; }
      const win = window.open("", "_blank", "width=900,height=700");
      if (!win) { window.print(); return; }

      const headStyles = Array.from(
        document.querySelectorAll('style, link[rel="stylesheet"]')
      ).map((node) => node.outerHTML).join("\n");

      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title}</title>
            ${headStyles}
            <style>
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
              @page { size: A4 portrait; margin: 8mm; }
              html, body { margin: 0; padding: 0; background: #ffffff; width: 210mm; min-height: 297mm; }
              .invoice-page, .statement-page { box-shadow: none !important; border-radius: 0 !important; margin: 0 auto !important; max-width: 100% !important; background: #ffffff !important; page-break-inside: avoid; break-inside: avoid; }
              .invoice-page { padding: 12mm !important; }
              .statement-page { padding: 14mm 12mm 10mm 12mm !important; }
              .invoice-table thead, .invoice-table th, .statement-table thead, .statement-table th { background: #0a1628 !important; color: #ffffff !important; }
              .invoice-table tbody tr.row-alt, .statement-table tbody tr.row-alt { background: #fafafa !important; }
              .invoice-table tbody tr.row-zero { background: #f7f7f7 !important; color: #888 !important; }
              .accent-line { background: #c0392b !important; }
              .company-name { color: #111111 !important; }
              .meta-label { color: #111111 !important; }
              .meta-value { color: #222222 !important; }
              .address, .address-line { color: #444444 !important; }
              .bank-details h4 { color: #111111 !important; }
              .bank-details p { color: #333333 !important; }
              .total-amount { color: #111111 !important; }
              .total-label { color: #333333 !important; }
              .contact-footer, .contact-line { color: #555555 !important; border-top-color: #eeeeee !important; }
              .divider { background: #dddddd !important; }
              * { page-break-inside: avoid !important; break-inside: avoid !important; }
              .no-print { display: none !important; }
            </style>
          </head>
          <body>${el.outerHTML}</body>
        </html>
      `);
      win.document.close();
      win.onload = () => {
        setTimeout(() => {
          win.focus();
          win.print();
          win.close();
        }, 300);
      };
    },

    // ============================================================
    //  EMAIL
    // ============================================================
    openEmailDialog() {
      this.emailForm.to = this.landlord?.email || "";
      this.emailForm.cc = "rent@gqproperty.co.za";
      this.emailForm.subject = `Invoice ${this.invoiceNumber} – ${this.selectedMonthsLabel} – GQ Property Group`;
      this.emailForm.message =
        `Dear ${this.landlordDisplayName},\n\n` +
        `Please find attached your invoice covering: ${this.selectedMonthsLabel}.\n\n` +
        `Invoice No: ${this.invoiceNumber}\n` +
        `Total Due: ${this.formatCurrency(this.totalDue)}\n\n` +
        `Kind regards,\nGQ Property Group`;
      this.emailDialog = true;
    },

    async buildInvoicePdfBase64() {
      const el = document.getElementById("invoice-print-area");
      if (!el) throw new Error("Invoice area not found");

      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      const canvas = await html2canvas(el, {
        scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff",
        logging: false, imageTimeout: 0, removeContainer: true,
      });

      window.scrollTo(scrollX, scrollY);
      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margin = 20;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      const scaleW = usableWidth / canvas.width;
      const scaleH = usableHeight / canvas.height;
      const scale = Math.min(scaleW, scaleH);
      const imgWidth = canvas.width * scale;
      const imgHeight = canvas.height * scale;
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight, undefined, "SLOW");
      return pdf.output("datauristring").split(",")[1];
    },

    buildEmailPayload(pdfBase64) {
      return {
        to: this.emailForm.to,
        cc: this.emailForm.cc || "rent@gqproperty.co.za",
        subject: this.emailForm.subject,
        text: this.emailForm.message,
        pdfBase64: pdfBase64,
      };
    },

    async sendEmail() {
      if (!this.emailForm.to || !this.emailForm.subject) return;
      this.sending = true;
      this.sendingStatus = "Generating PDF…";

      let historyId = null;

      try {
        const visibleItems = this.displayItems.map((it) => {
          const { _index, _displayIndex, _key, _isZero, ...rest } = it;
          return rest;
        });

        const pdfBase64 = await this.buildInvoicePdfBase64();
        const nowStamp = this.buildTimestamp();
        const pdfFileName = `Invoice_${this.invoiceNumber}_${nowStamp}.pdf`;

        const invoicedPaymentIds = Array.isArray(this._usedPaymentIds) ? [...this._usedPaymentIds] : [];
        const invoicedPaymentRefs = Array.isArray(this._usedPaymentRefs) ? [...this._usedPaymentRefs] : [];

        this.sendingStatus = "Saving to history…";
        const historyDoc = await addDoc(collection(db, HISTORY_COLLECTION), {
          landlordId: this.landlord.id,
          landlordReference: this.landlord.reference,
          landlordName: this.landlordDisplayName,
          invoiceNumber: this.invoiceNumber,
          sequence: this.invoiceSequence,
          months: Array.from(new Set(this.items.map((it) => it.monthKey))).sort(),
          monthsLabel: this.selectedMonthsLabel,
          periodFrom: this.periodFrom,
          periodTo: this.showOnlyPositive ? this.periodTo : this.endOfCurrentMonthPlusOneDay(),
          leasePeriods: this.leaseWindows.map((lw) => ({
            leaseRef: lw.leaseRef,
            from: lw.from,
          })),
          invoicedPaymentIds,
          invoicedPaymentRefs,
          timestamp: nowStamp,
          to: this.emailForm.to,
          cc: this.emailForm.cc || "",
          subject: this.emailForm.subject,
          message: this.emailForm.message,
          total: this.totalDue,
          itemCount: visibleItems.length,
          items: visibleItems,
          allItems: this.items,
          overrideCount: this.overrideCount,
          pdfFileName: pdfFileName,
          status: "pending",
          sentAt: serverTimestamp(),
          sentBy: localStorage.getItem("email") || "unknown",
        });
        historyId = historyDoc.id;

        this.sendingStatus = "Sending email…";
        const payload = this.buildEmailPayload(pdfBase64);

        const response = await fetch(EMAIL_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errBody = await response.text();
          throw new Error(`Endpoint returned ${response.status}: ${errBody.slice(0, 200)}`);
        }

        if (historyId) {
          try {
            await updateDoc(doc(db, HISTORY_COLLECTION, historyId), {
              status: "sent",
              sentAt: serverTimestamp(),
            });
          } catch (e) {}
        }

        await logEvent(
          "Landlord Invoice Emailed",
          `${this.landlordDisplayName} (${this.landlord.reference}) · ${this.invoiceNumber} → ${this.emailForm.to}`,
          "Landlord Management"
        );

        this.snackbar = {
          show: true,
          text: `Invoice ${this.invoiceNumber} sent to ${this.emailForm.to}`,
          color: "success",
        };
        this.emailDialog = false;
        await this.loadSendHistory();
        await this.loadInvoicePeriod();

        this.invoiceSequence = (this.invoiceSequence || 1) + 1;
        this.invoiceNumber = this.buildInvoiceNumber(this.invoiceSequence);
      } catch (e) {
        console.error("Send email failed:", e);
        if (historyId) {
          try {
            await updateDoc(doc(db, HISTORY_COLLECTION, historyId), {
              status: "failed",
              error: String(e.message || e),
              sentAt: serverTimestamp(),
            });
          } catch (err) {}
        }
        this.snackbar = { show: true, text: `Failed to send: ${e.message}`, color: "error" };
      } finally {
        this.sending = false;
        this.sendingStatus = "";
      }
    },

    // ============================================================
    //  HISTORY
    // ============================================================
    async loadSendHistory() {
      if (!this.landlord) return;
      this.loadingHistory = true;
      try {
        const q = query(
          collection(db, HISTORY_COLLECTION),
          where("landlordId", "==", this.landlord.id),
          orderBy("sentAt", "desc"),
          limit(50)
        );
        const snap = await getDocs(q);
        this.sendHistory = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.warn("History load failed:", e.message);
        this.sendHistory = [];
      } finally {
        this.loadingHistory = false;
      }
    },

    openHistoryEntry(h) {
      const historicalItems = Array.isArray(h.allItems) && h.allItems.length > 0
        ? h.allItems
        : (Array.isArray(h.items) ? h.items : []);

      this.items = historicalItems.map((it) => ({
        leaseRef: it.leaseRef || "",
        monthKey: it.monthKey || "",
        tenantName: it.tenantName || "",
        description: it.description || "",
        percent: Number(it.percent) || 0,
        rentPaid: Number(it.rentPaid) || 0,
        unitPrice: Number(it.unitPrice) || 0,
        total: Number(it.total) || 0,
        calculatedTotal: Number(it.calculatedTotal) || Number(it.total) || 0,
        customPercent: !!it.customPercent,
        customAmount: !!it.customAmount,
      }));

      this._chargeCache = this.items.map((it) => ({ ...it }));

      this.invoiceNumber = h.invoiceNumber || this.invoiceNumber;

      if (h.periodFrom) this.periodFrom = h.periodFrom;
      if (h.periodTo) this.periodTo = h.periodTo;

      this.autoPeriod = false;
      this.showOnlyPositive = false;
      this.viewingHistoryInvoice = true;
      this.viewingHistoryId = h.id;

      this.activeTab = 0;
    },

    exitHistoryView() {
      this.viewingHistoryInvoice = false;
      this.viewingHistoryId = null;
      this.autoPeriod = true;
      this.showOnlyPositive = true;
      this.periodFrom = "";
      this.periodTo = "";

      this.loadInvoicePeriod().then(() => {
        this.generateInvoice();
        this.invoiceSequence = this.invoiceSequence || 1;
        this.invoiceNumber = this.buildInvoiceNumber(this.invoiceSequence);
      });
    },

    resendHistoryEntry(h) {
      this.emailForm.to = h.to || this.landlord?.email || "";
      this.emailForm.cc = h.cc || "rent@gqproperty.co.za";
      this.emailForm.subject = h.subject || `Invoice ${h.invoiceNumber} – ${this.historyPeriodLabel(h)} – GQ Property Group`;
      this.emailForm.message = h.message ||
        `Dear ${this.landlordDisplayName},\n\nPlease find attached your commission invoice.\n\nKind regards,\nGQ Property Group`;
      this.emailDialog = true;
    },

    confirmDeleteHistory(h) { this.invoiceToDelete = h; this.deleteDialog = true; },
    confirmDeleteStatement(row) { this.invoiceToDelete = row; this.deleteDialog = true; },

    async executeDelete() {
      if (!this.invoiceToDelete) return;
      this.deleting = true;
      try {
        const id = this.invoiceToDelete.id;
        const invoiceNo = this.invoiceToDelete.invoiceNumber || this.invoiceToDelete.id;

        await deleteDoc(doc(db, HISTORY_COLLECTION, id));
        this.sendHistory = this.sendHistory.filter((x) => x.id !== id);
        this.statementRows = this.statementRows.filter((x) => x.id !== id);

        await logEvent(
          "Landlord Invoice Record Deleted",
          `${this.landlordDisplayName} (${this.landlord.reference}) · ${invoiceNo}`,
          "Landlord Management"
        );

        this.snackbar = { show: true, text: `Invoice ${invoiceNo} deleted`, color: "success" };
        this.deleteDialog = false;
        this.invoiceToDelete = null;

        await this.loadInvoicePeriod();

        if (this.viewingHistoryId === id) {
          this.exitHistoryView();
        }
      } catch (e) {
        console.error("Delete failed:", e);
        this.snackbar = { show: true, text: `Delete failed: ${e.message}`, color: "error" };
      } finally {
        this.deleting = false;
      }
    },

    // ============================================================
    //  STATEMENT
    // ============================================================
    formatPeriodLabel(isoMonth) {
      if (!isoMonth) return "";
      const [y, m] = isoMonth.split("-").map(Number);
      const d = new Date(y, m - 1, 1);
      return d.toLocaleDateString("en-ZA", { year: "numeric", month: "long" });
    },

    async loadStatement() {
      if (!this.landlord) return;
      this.loadingStatement = true;
      this.statementRows = [];

      try {
        const startIso = this.statementStartDate;
        const endIso = this.statementEndDate;
        const startIsoFull = `${startIso}-01`;
        const endIsoFull = `${endIso}-31`;

        const all = await getDocs(
          query(
            collection(db, HISTORY_COLLECTION),
            where("landlordId", "==", this.landlord.id),
            orderBy("sentAt", "asc"),
            limit(500)
          )
        );

        const rows = [];
        let running = 0;

        all.docs.forEach((d) => {
          const h = d.data();

          let inRange = false;
          if (h.periodFrom && h.periodTo) {
            inRange = !(h.periodTo < startIsoFull || h.periodFrom > endIsoFull);
          } else {
            const invMonths = Array.isArray(h.months) && h.months.length > 0
              ? h.months
              : (h.month ? [h.month] : []);
            if (invMonths.length === 0) return;
            inRange = invMonths.some((m) => {
              const iso = this.isoMonthFromKey(m);
              return iso && iso >= startIso && iso <= endIso;
            });
          }
          if (!inRange) return;

          const sentDate = h.sentAt && typeof h.sentAt.toDate === "function"
            ? h.sentAt.toDate() : h.sentAt ? new Date(h.sentAt) : null;

          const total = Number(h.total) || 0;
          if (h.status === "sent") running += total;

          rows.push({
            id: d.id,
            dateSent: sentDate
              ? sentDate.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "2-digit" })
              : "—",
            invoiceNumber: h.invoiceNumber || "—",
            monthsLabel: this.historyPeriodLabel(h),
            itemCount: h.itemCount || 0,
            total,
            status: h.status || "pending",
            runningTotal: running,
          });
        });

        this.statementRows = rows;
      } catch (e) {
        console.error("Statement failed:", e);
        this.statementRows = [];
      } finally {
        this.loadingStatement = false;
      }
    },

    // ============================================================
    //  FORMATTERS
    // ============================================================
    formatCurrency(value) {
      if (value == null || value === "" || isNaN(value)) return "R0.00";
      return "R" + Number(value).toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDateTime(ts) {
      if (!ts) return "—";
      let d;
      if (typeof ts.toDate === "function") d = ts.toDate();
      else d = new Date(ts);
      return d.toLocaleString("en-ZA", {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
* {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  color-adjust: exact !important;
}

.invoice-wrapper {
  background: #f0f0f0;
  min-height: 100vh;
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
}

.tabs-wrapper { max-width: 100%; margin: 0 0 16px 0; }

.invoice-tabs { border-bottom: 2px solid #e0e0e0; width: 100%; }

.invoice-tabs .v-tab {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  color: #555 !important;
}

.invoice-tabs .v-tab--active { color: #0a1628 !important; }
.invoice-tabs .v-tab--active::before { background-color: #0a1628 !important; }

.tabs-content { background: transparent !important; width: 100%; }
.controls-bar { max-width: 100%; margin: 0 0 24px 0; }

.actions-cell { white-space: nowrap; }
.actions-cell .v-btn { margin: 0 1px; }
.actions-cell .v-icon { font-size: 20px !important; }

.qty-cell { white-space: nowrap; }
.percent-edit-btn { opacity: 0.25; transition: opacity 0.15s ease; }
.invoice-table tbody tr:hover .percent-edit-btn { opacity: 1; }
.custom-chip { font-weight: 700 !important; letter-spacing: 0.4px; text-transform: none !important; }

.invoice-table tbody tr.row-zero { background: #f7f7f7 !important; }
.invoice-table tbody tr.row-zero td { color: #888888 !important; }
.invoice-table tbody tr.row-zero .col-desc { font-style: italic; }

.invoice-page {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  padding: 36px 44px 32px 44px;
  background: #ffffff;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  color: #1a1a1a;
  box-sizing: border-box;
}

.header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
.company-name { margin: 0 0 5px 0; font-size: 24px; font-weight: 700; color: #111111; letter-spacing: -0.3px; line-height: 1.15; }
.address { margin: 0; font-size: 12px; line-height: 1.4; color: #444444; }
.logo { flex-shrink: 0; margin-left: 20px; }
.logo-img { width: 88px; height: 88px; object-fit: contain; display: block; }
.accent-line { height: 2px; background: #c0392b; margin: 14px 0 20px 0; }

.meta-section { display: flex; justify-content: space-between; align-items: flex-start; gap: 40px; margin-bottom: 14px; }
.date-block { flex: 0 0 auto; min-width: 140px; }
.to-block { flex: 1 1 auto; min-width: 200px; }
.invoice-no-block { flex: 0 0 auto; text-align: right; min-width: 180px; }
.meta-label { margin: 0 0 3px 0; font-size: 12px; font-weight: 700; color: #111111; }
.meta-value { margin: 0; font-size: 13px; color: #222222; line-height: 1.35; }
.landlord-name { font-weight: 600; }
.address-line { color: #444444; font-size: 12px; }
.invoice-number-inline { font-size: 13px; white-space: nowrap; }
.invoice-number-inline strong { color: #111111; font-weight: 700; }
.divider { height: 1px; background: #dddddd; margin-bottom: 0; }

.invoice-table { width: 100%; border-collapse: collapse; margin-top: 0; font-size: 13px; }
.invoice-table thead { background: #0a1628; color: #ffffff; }
.invoice-table th { padding: 9px 12px; text-align: left; font-weight: 600; font-size: 11px; letter-spacing: 0.4px; border: none; background: #0a1628; color: #ffffff; }
.invoice-table th.col-price, .invoice-table th.col-total, .invoice-table td.col-price, .invoice-table td.col-total { text-align: right; }
.invoice-table th.col-qty, .invoice-table td.col-qty { width: 140px; }
.invoice-table th.col-price, .invoice-table td.col-price { width: 120px; }
.invoice-table th.col-total, .invoice-table td.col-total { width: 120px; }
.invoice-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.invoice-table tbody tr.row-alt { background: #fafafa; }
.invoice-table td { padding: 10px 12px; color: #222222; vertical-align: middle; }
.empty-row { text-align: center !important; color: #999999 !important; padding: 24px 12px !important; font-style: italic; }

.total-row { display: flex; justify-content: flex-end; align-items: center; gap: 30px; padding: 12px 12px 12px 0; border-top: 1px solid #cccccc; font-size: 14px; }
.total-label { font-weight: 600; color: #333333; }
.total-amount { font-weight: 700; font-size: 15px; color: #111111; min-width: 100px; text-align: right; }

.footer-section { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 32px; }
.bank-details h4 { margin: 0 0 6px 0; font-size: 13px; font-weight: 700; color: #111111; }
.bank-details p { margin: 0 0 2px 0; font-size: 12px; color: #333333; line-height: 1.35; }
.thank-you { font-size: 14px; font-weight: 500; color: #333333; margin-top: 4px; }

.contact-footer { display: flex; justify-content: space-between; margin-top: 32px; padding-top: 12px; border-top: 1px solid #eeeeee; font-size: 11px; color: #555555; }

.golden-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000 !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  text-transform: none !important;
}

.statement-page {
  max-width: 100%; margin: 0; padding: 32px 40px; background: #ffffff;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  color: #1a1a1a; box-sizing: border-box;
}
.statement-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
.statement-meta { display: flex; justify-content: space-between; gap: 40px; margin-bottom: 20px; }
.statement-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.statement-table thead { background: #0a1628; color: #ffffff; }
.statement-table th { padding: 10px 12px; text-align: left; font-weight: 600; font-size: 11px; letter-spacing: 0.4px; border: none; background: #0a1628; color: #ffffff; }
.statement-table th.col-total, .statement-table th.col-balance, .statement-table td.col-total, .statement-table td.col-balance, .statement-table td.text-right { text-align: right; }
.statement-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.statement-table tbody tr.row-alt { background: #fafafa; }
.statement-table td { padding: 10px 12px; color: #222222; vertical-align: middle; }
.statement-table .col-date { width: 110px; }
.statement-table .col-inv { width: 150px; }
.statement-table .col-month { width: 180px; }
.statement-table .col-status { width: 90px; }
.statement-table .col-balance { width: 140px; }
.statement-table .col-total { width: 130px; }
.statement-table .col-actions { width: 90px; }
.statement-total-row { background: #f0f0f0; font-weight: 700; border-top: 2px solid #0a1628; }
.statement-total-row td { padding: 12px; font-size: 14px; color: #111111; }
.status-sent { color: #2e7d32; font-weight: 600; }
.status-pending { color: #ef6c00; font-weight: 600; }
.status-failed { color: #c62828; font-weight: 600; }
.statement-footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #eeeeee; font-size: 12px; color: #555555; }
.statement-footer .contact-line { display: flex; justify-content: space-between; margin-top: 8px; }

@media print {
  .no-print { display: none !important; }
  .tabs-wrapper, .v-tabs-items > .v-tab-item:not(:first-child) { display: none !important; }
  .invoice-wrapper { background: #fff; padding: 0; width: 100%; }
  .invoice-page, .statement-page { box-shadow: none; border-radius: 0; padding: 20px 30px; max-width: 100%; }
  .statement-table .col-actions { display: none !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
}

@media (max-width: 600px) {
  .invoice-wrapper { padding: 12px; }
  .invoice-page { padding: 22px 18px; }
  .statement-page { padding: 22px 18px; }
  .header, .statement-header { flex-direction: column; gap: 14px; }
  .logo-img { width: 72px; height: 72px; }
  .meta-section { flex-direction: column; gap: 12px; }
  .invoice-no-block { text-align: left; min-width: auto; }
  .statement-meta { flex-direction: column; gap: 12px; }
  .footer-section { flex-direction: column; gap: 16px; }
  .contact-footer, .contact-line { flex-direction: column; gap: 6px; }
  .invoice-table, .statement-table { font-size: 11px; }
  .invoice-table th, .invoice-table td, .statement-table th, .statement-table td { padding: 8px 6px; }
}
</style>